import dayjs from 'dayjs';
import { DateFormats } from 'domains/reports/domain/types/ReportConst';

/**
 * Default start hour for reports (00:00:00 - midnight)
 */
export const DEFAULT_START_HOUR = '00:00:00';

/**
 * Default end hour for reports (23:59:59 - end of day)
 */
export const DEFAULT_END_HOUR = '23:59:59';

/**
 * Time option structure for select components
 */
export interface TimeOption {
  /** Time in 24-hour format (HH:mm:ss) */
  value: string;
  /** Time in 12-hour format with AM/PM (hh:mm AM/PM) */
  label: string;
}

export const formatDate = (date: Date): string => {
  return dayjs(date).format(DateFormats['DD/MM/YYYY']);
};

/**
 * Formats a 24-hour time string to 12-hour format with AM/PM
 *
 * @param {string} hour - Time string in 24-hour format (HH:mm:ss)
 * @returns {string} Time string in 12-hour format with AM/PM
 */
export const formatHour12 = (hour: string): string => {
  return dayjs(hour, 'HH:mm:ss').format('hh:mm A');
};

export const formatHour = (hour: number): string => {
  if (hour === 0) return '12';
  if (hour > 12) return String(hour - 12).padStart(2, '0');
  return String(hour).padStart(2, '0');
};

export const formatMinute = (minute: number): string => {
  return String(minute).padStart(2, '0');
};

export const getPeriod = (hour: number): 'AM' | 'PM' => {
  return hour >= 12 ? 'PM' : 'AM';
};

/**
 * Generates time options for select components in 30-minute intervals
 *
 * @returns {TimeOption[]} Array of time options with value (24hr) and label (12hr)
 *
 * @example
 * const options = generateTimeOptions();
 * // [
 * //   { value: "00:00:00", label: "12:00 AM" },
 * //   { value: "00:30:00", label: "12:30 AM" },
 * //   { value: "21:00:00", label: "09:00 PM" },
 * //   ...
 * // ]
 */
export const generateTimeOptions = (): TimeOption[] => {
  const options: TimeOption[] = [];

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const period = getPeriod(hour);
      const displayHour = formatHour(hour);
      const displayMinute = formatMinute(minute);

      const label = `${displayHour}:${displayMinute} ${period}`;
      const value = `${hour.toString().padStart(2, '0')}:${displayMinute}:00`;

      options.push({ value, label });
    }
  }

  // Add final time of day
  options.push({ value: '23:59:59', label: '11:59 PM' });

  return options;
};

/**
 * Generates time options for select components in 1-hour intervals
 *
 * @returns {TimeOption[]} Array of time options with value (24hr) and label (12hr) for every full hour
 *
 * @example
 * const options = generateFullTimeOptions();
 * // [
 * //   { value: "00:00:00", label: "12:00 AM" },
 * //   { value: "01:00:00", label: "01:00 AM" },
 * //   { value: "21:00:00", label: "09:00 PM" },
 * //   { value: "23:00:00", label: "11:00 PM" },
 * // ]
 */
export const generateFullTimeOptions = (): TimeOption[] => {
  const options: TimeOption[] = [];

  for (let hour = 0; hour < 24; hour++) {
    const period = getPeriod(hour);
    const displayHour = formatHour(hour);
    const displayMinute = '00';

    const label = `${displayHour}:${displayMinute} ${period}`;
    const value = `${hour.toString().padStart(2, '0')}:${displayMinute}:00`;

    options.push({ value, label });
  }

  // Add final time of day
  options.push({ value: '23:59:59', label: '11:59 PM' });

  return options;
};
